import {
  CreditCard, Hourglass, Landmark, ReceiptText, RotateCw,
} from "lucide-react";
import { useRef } from "react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/Button";
import { NEW_PAYMENT_METHOD_NOTICE } from "../../configs/unpaidInvoices.config";
import { useReservedBannerHeight } from "../../hooks/unpaid/useReservedBannerHeight";
import { pendingNoticeParts, unpaidNoticeParts } from "../../utils/unpaidAccess.utils";
import type { UnpaidBannerProps, UnpaidNoticeProps } from "../../types/unpaidInvoices.types";

type BannerMessageProps = Omit<UnpaidBannerProps, "payAction" | "offerState" | "onRetryOffer" | "onReviewTransfer" | "onChangeAccount">;

// The explicit dark: text colour is needed because the warning variant carries its own dark: override.
const PAY_BUTTON_CLASSES = cn(
  "border-warning-foreground bg-warning-foreground text-background shadow-xs",
  "hover:bg-warning-foreground/90 dark:text-background",
);

type SupportSentenceProps = Pick<UnpaidNoticeProps, "onContactSupport">;

const SupportSentence = ({ onContactSupport }: SupportSentenceProps) => (
  <>
    {" S'il s'agit d'une erreur, "}
    <button type="button" className="cursor-pointer font-medium underline underline-offset-2" onClick={onContactSupport}>
      contactez le support
    </button>
    .
  </>
);

const BannerMessage = ({
  amountCents, pendingCents, pendingPayment, isAdmin, onContactSupport, paymentMethodRequired, staffMessage,
}: BannerMessageProps) => {
  const { lead, amount, tail } = pendingPayment
    ? pendingNoticeParts(pendingPayment)
    : unpaidNoticeParts(amountCents, pendingCents);

  return (
    <p className="text-sm">
      {lead}
      <strong className="font-semibold tabular-nums">{amount}</strong>
      {tail}
      {paymentMethodRequired && ` ${NEW_PAYMENT_METHOD_NOTICE}`}
      {staffMessage && ` ${staffMessage}`}
      {!isAdmin && !pendingPayment && " Demandez à un élu administrateur du CSE de le régler."}
      <SupportSentence onContactSupport={onContactSupport} />
    </p>
  );
};

type BannerActionProps = Pick<UnpaidNoticeProps, "payAction" | "offerState" | "onRetryOffer">;

const RetryOfferButton = ({ offerState, onRetryOffer }: Omit<BannerActionProps, "payAction">) => {
  const isRetrying = offerState === "retrying";

  return (
    <Button
      type="button"
      variant="warning"
      size="sm"
      className={cn("shrink-0", PAY_BUTTON_CLASSES)}
      disabled={isRetrying}
      onClick={onRetryOffer}
    >
      <RotateCw className={cn(isRetrying && "motion-safe:animate-spin")} aria-hidden />
      Réessayer
    </Button>
  );
};

const PayNowButton = ({ payAction }: Pick<UnpaidNoticeProps, "payAction">) => {
  if (!payAction) return null;

  return (
    <Button
      type="button"
      variant="warning"
      size="sm"
      className={cn("shrink-0", PAY_BUTTON_CLASSES)}
      onClick={payAction.open}
    >
      <CreditCard aria-hidden />
      Régler maintenant
    </Button>
  );
};

const ReviewTransferButton = ({ onReviewTransfer }: Pick<UnpaidBannerProps, "onReviewTransfer">) => {
  if (!onReviewTransfer) return null;

  return (
    <Button
      type="button"
      variant="warning"
      size="sm"
      className={cn("h-auto shrink-0 whitespace-normal text-left", PAY_BUTTON_CLASSES)}
      onClick={onReviewTransfer}
    >
      <Landmark aria-hidden />
      Revoir les infos bancaires pour le virement
    </Button>
  );
};

const ChangeAccountButton = ({ onChangeAccount }: Pick<UnpaidNoticeProps, "onChangeAccount">) => {
  if (!onChangeAccount) return null;

  return (
    <Button
      type="button"
      variant="warning"
      size="sm"
      className={cn("shrink-0", PAY_BUTTON_CLASSES)}
      onClick={onChangeAccount}
    >
      <Landmark aria-hidden />
      Renseigner un nouveau RIB
    </Button>
  );
};

const BannerAction = ({ payAction, offerState, onRetryOffer }: BannerActionProps) => (
  offerState === "failed" || offerState === "retrying"
    ? <RetryOfferButton offerState={offerState} onRetryOffer={onRetryOffer} />
    : <PayNowButton payAction={payAction} />
);

const UnpaidBanner = ({
  payAction, offerState, onRetryOffer, onReviewTransfer, onChangeAccount, ...messageProps
}: UnpaidBannerProps) => {
  const bannerRef = useRef<HTMLElement>(null);
  useReservedBannerHeight(bannerRef);

  const isPending = messageProps.pendingPayment !== null;
  const StatusIcon = isPending ? Hourglass : ReceiptText;

  return (
    <section
      ref={bannerRef}
      aria-label="Règlement en attente"
      className="fixed inset-x-0 top-0 z-40 shadow-sm"
    >
      <div className="bg-background">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-warning/40 bg-warning/15 px-4 py-3 sm:px-6 text-warning-foreground">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-warning/25 text-warning-foreground">
            <StatusIcon className="size-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1 basis-60">
            <BannerMessage {...messageProps} />
          </div>
          <ChangeAccountButton onChangeAccount={onChangeAccount} />
          {messageProps.isAdmin && (isPending
            ? <ReviewTransferButton onReviewTransfer={onReviewTransfer} />
            : <BannerAction payAction={payAction} offerState={offerState} onRetryOffer={onRetryOffer} />)}
        </div>
      </div>
    </section>
  );
};

export default UnpaidBanner;
